import { useEffect, useState } from 'react';
import { Link } from 'wouter';

import { Header } from '~/client/components/Header';
import { Icon } from '~/client/components/Icons';
import { Button, Chip } from '~/client/components/Primitives';
import { forgetPoll, listPolls, type PollSummary } from '~/client/lib/myPolls';

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} ${day === 1 ? 'day' : 'days'} ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month} ${month === 1 ? 'mo' : 'mos'} ago`;
  const year = Math.floor(day / 365);
  return `${year} ${year === 1 ? 'yr' : 'yrs'} ago`;
}

const ROLE_COPY: Record<PollSummary['role'], { label: string; variant: 'lime' | 'blue' | 'soft' }> =
  {
    owner: { label: 'yours', variant: 'lime' },
    respondent: { label: 'replied', variant: 'blue' },
    visitor: { label: 'visited', variant: 'soft' },
  };

export function MyPolls() {
  const [polls, setPolls] = useState<PollSummary[]>([]);

  useEffect(() => {
    setPolls(listPolls());
  }, []);

  const refresh = () => setPolls(listPolls());

  const handleForget = (id: string) => {
    forgetPoll(id);
    refresh();
  };

  return (
    <>
      <Header />
      <main className="app-main">
        <div className="container container-narrow">
          <h1 className="page-title">my polls</h1>
          <p className="muted" style={{ marginTop: 0, marginBottom: 24 }}>
            polls you've made or replied to from this browser. nothing's stored on the server — wipe
            your browser data and this list goes too.
          </p>

          {polls.length === 0 ? (
            <div className="card-surface" style={{ padding: 32, textAlign: 'center' }}>
              <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>
                nothing here yet. make a poll or open a link a friend sent you.
              </p>
              <Link href="/new">
                <Button variant="primary">
                  Make a free poll <Icon name="arrow" size={14} />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="col" style={{ gap: 8 }}>
              {polls.map((p) => {
                const meta = ROLE_COPY[p.role];
                return (
                  <div key={p.id} className="card-surface my-poll-row">
                    <Link href={`/p/${p.id}`} className="my-poll-link">
                      <div className="my-poll-title">{p.title || '(untitled poll)'}</div>
                      <div className="my-poll-meta">
                        <Chip variant={meta.variant}>{meta.label}</Chip>
                        <span className="mono muted">/{p.id}</span>
                        <span className="muted">·</span>
                        <span className="muted">{timeAgo(p.lastVisited)}</span>
                      </div>
                    </Link>
                    <button
                      type="button"
                      className="btn btn-text btn-sm my-poll-forget"
                      onClick={() => handleForget(p.id)}
                      aria-label={`forget ${p.title}`}
                    >
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
